/* eslint-disable @typescript-eslint/no-explicit-any */
import React, { useState, useEffect, useCallback } from 'react';
import CommanToolTip from '../CommanToolTip';
import {
  formatAmount,
  webFormatCurrency,
  RootState,
  useProductName,
  useraccountDetailsRequest,
  renderDescriptionParagraphs,
} from '@rb-serve-app/core';
import { RightArrow } from '../../assets/svg/ArrowIcon';
import moment from 'moment';
import { EventType, useAnalytics } from '../../ui/Analytics';
import { useDispatch, useSelector } from 'react-redux';
import Spinner from '../Spinner';
import { useNavigate } from 'react-router-dom';
import { logBrazeCustomEvent } from '../../config/braze';

type Props = {
  isLoading?: boolean;
  contentfulData: any;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  assets: any;
  featureFlaggingData: any;
  setShowFeatureFlagMessage: React.Dispatch<React.SetStateAction<boolean>>;
};

const indexMapping = (index: number, name: string) => {
  if (name === 'min_payment') {
    return 'minimum payments | tooltip';
  } else {
    switch (index) {
      case 0:
        return 'statment balance | tooltip';
      case 1:
        return 'available credit | tooltip';
      case 2:
        return 'credit limit | tooltip';
      case 3:
        return 'payment due date | tooltip';
      case 4:
        return 'last payment | tooltip';
      case 5:
        return 'last payment received on | tooltip';
    }
  }
};

const AccountDetails: React.FC<Props> = ({
  contentfulData,
  assets,
  featureFlaggingData,
  setShowFeatureFlagMessage,
}) => {
  const { trackEvent } = useAnalytics();
  const [activeTooltip, setActiveTooltip] = useState<number | null>(null);
  const handleTooltipToggle = (index: number, name: string) => {
    const mappedName = indexMapping(index, name) as string;
    trackClickEvent(mappedName);
    setActiveTooltip((prev) => (prev === index ? null : index));
  };
  const { productName } = useProductName();
  const { accountId, customerId, accountDetails } = useSelector(
    (state: RootState) => state.user
  );

  const productWithCardLast4 = [
    productName,
    accountDetails?.customer?.cardLast4,
  ]
    ?.filter(Boolean)
    ?.join(' ');
  const navigation = useNavigate();
  const trackClickEvent = (name: string) => {
    trackEvent(
      EventType.CLICK_EVENT,
      'account details',
      window.location.href,
      `account details|${name}`
    );
  };

  const featureFlag = () => {
    if (window.scrollY !== 0) {
      window.scrollTo({
        top: 0,
        behavior: 'smooth',
      });
    }
    trackClickEvent('manage alerts');
    if (featureFlaggingData) {
      navigation('/manage-alerts');
    } else {
      setShowFeatureFlagMessage(true);
      setTimeout(() => {
        setShowFeatureFlagMessage(false);
      }, 3000);
    }
  };

  useEffect(() => {
    trackEvent(EventType.PAGE_VIEW, 'account details', window.location.href);
    logBrazeCustomEvent('viewed_account_details');
  }, []);

  const dispatch = useDispatch();
  const lang = localStorage.getItem('language') || 'en-US';

  const fetchAccountDetails = useCallback(() => {
    if (accountId) {
      if (!accountDetails?.customer?.cardLast4) {
        dispatch(useraccountDetailsRequest({ accountId, customerId }));
      }
    }
  }, [accountId, customerId, dispatch]);
  useEffect(() => {
    fetchAccountDetails();
  }, [fetchAccountDetails]);

  if (!accountDetails || Object.keys(accountDetails).length === 0) {
    return (
      <div className="flex justify-center items-center h-[80vh]">
        <Spinner />
      </div>
    );
  }

  return (
    <div
      className="p-6 bg-white rounded-lg min-h-[70vh]"
      role="region"
      aria-labelledby="account-details-heading"
    >
      <h2
        id="account-details-heading"
        className="font-semibold text-[16px] md:text-[22px] mb-4 px-5 sm:px-4 md:px-5 lg:px-0 xl:px-0"
        aria-label={contentfulData?.accountDetailsTitle}
      >
        {contentfulData?.accountDetailsTitle}
      </h2>
      {/* <div className="flex gap-12 md:items-center lg:items-center border-b border-gray-200 pb-8 mb-8 flex-col lg:flex-row flex-1  items-start"> */}
      <div className="flex gap-12 xl:px-0 lg:px-0 py-4 sm:px-4 flex-1 overflow-y-scroll scrollbar-hide md:items-center lg:items-center border-b border-gray-200 pb-8  mb-8 flex-col  xl:flex-row items-start w-full">
        {/* Card Section */}
        <section
          aria-labelledby="card-section-heading"
          className={`bg-primary-red text-white px-5 lg:px-12 md:px-12 md:py-12 py-5 md:rounded-lg
          ipad-landscape:min-w-[308px] md:max-w-[442px] ipad-portrait:min-w-[274px]
        lg:h-[263px] lg:max-h-[263px]  md:h-[243px] md:max-h-[243px] lg:flex-[0.36] flex flex-col items-center w-full ${lang == 'fr' && 'text-center'}`}
        >
          <h3 id="card-section-heading" className="sr-only">
            {contentfulData?.accountDetailsTitle}
          </h3>
          <div
            aria-label={renderDescriptionParagraphs(
              productWithCardLast4,
              undefined,
              true
            )}
          >
            {renderDescriptionParagraphs(
              productWithCardLast4,
              'font-extralight text-sm mb-[20px]'
            )}
          </div>
          <p
            className="text-xl sm:text-[17px] font-extralight mb-[3px]"
            aria-label={contentfulData?.currentBalanceTitle}
          >
            {contentfulData?.currentBalanceTitle}
          </p>
          <h2
            className="text-3xl font-bold mb-[14px]"
            aria-label={accountDetails?.currentBalance?.value}
          >
            {Number(accountDetails?.currentBalance?.value) < 0
              ? `-${webFormatCurrency(Math.abs(Number(accountDetails?.currentBalance?.value)), lang)}`
              : webFormatCurrency(
                  Number(accountDetails?.currentBalance?.value),
                  lang
                ) || '--'}
          </h2>
          <div>
            <CommanToolTip
              label={
                `${contentfulData?.minimumPaymentTitle} ${webFormatCurrency(accountDetails?.statementDueAmount?.value, lang)}` ||
                '--'
              }
              tooltipText={
                contentfulData?.minimum_payment_info_icon_tooltip_text
              }
              labelColor="text-white"
              fill="#ffffff"
              gap="1"
              labelWeight="font-extralight"
              display="flex"
              isVisible={activeTooltip === 1}
              onToggle={() => handleTooltipToggle(1, 'min_payment')}
            />
          </div>
        </section>

        {/* Account Info */}
        <section
          aria-labelledby="account-info-heading"
          className="lg:col-span-2 space-y-4 lg:flex-[0.65] w-[100%]"
        >
          <h3 id="account-info-heading" className="sr-only">
            Account Information
          </h3>
          <div className="grid sm:grid-cols-1  xl:grid-cols-2  lg:grid-cols-3 md:grid-cols-2 gap-x-11 gap-y-[15px] md:gap-auto">
            {[
              {
                label: contentfulData?.statementBalanceTitle,
                tootiptext: contentfulData?.statementBalanceInfoIconTooltipText,
                value:
                  Number(accountDetails?.statementBalance?.value) < 0
                    ? `-${webFormatCurrency(Math.abs(Number(accountDetails?.statementBalance?.value)), lang)}`
                    : webFormatCurrency(
                        Number(accountDetails?.statementBalance?.value),
                        lang
                      ) || '--',
              },
              {
                label: contentfulData?.availableCreditTitle,
                tootiptext: contentfulData?.availableCreditInfoIconTooltipText,
                value:
                  Number(accountDetails?.realtimeBalance?.value) < 0
                    ? ` -${webFormatCurrency(Math.abs(Number(accountDetails?.realtimeBalance?.value)), lang)}`
                    : ` ${webFormatCurrency(Number(accountDetails?.realtimeBalance?.value), lang)}` ||
                      '--',
              },
              {
                label: contentfulData?.creditLimitTitle,
                tootiptext: contentfulData?.creditLimitInfoIconTooltipText,
                value:
                  accountDetails?.creditLimit?.value != null &&
                  !isNaN(accountDetails?.creditLimit?.value)
                    ? Number(accountDetails?.creditLimit?.value) < 0
                      ? ` -${webFormatCurrency(Math.abs(Number(accountDetails?.creditLimit?.value)), lang)}`
                      : ` ${webFormatCurrency(Number(accountDetails?.creditLimit?.value), lang)}`
                    : `${webFormatCurrency(0, lang)}`,
              },
              {
                label: contentfulData?.paymentDueDateTitle,
                tootiptext: contentfulData?.paymentDueDateInfoIconTooltipText,
                value: moment(accountDetails?.paymentDueDate).isValid()
                  ? moment(accountDetails?.paymentDueDate).format('MMM D, YYYY')
                  : '--',
              },
              {
                label: contentfulData?.lastPaymentTitle,
                tootiptext: contentfulData?.lastPaymentInfoIconTooltipText,
                value:
                  accountDetails?.lastPayment?.value != null &&
                  !isNaN(accountDetails?.lastPayment?.value)
                    ? Number(accountDetails?.lastPayment?.value) < 0
                      ? ` -${webFormatCurrency(Math.abs(Number(accountDetails?.lastPayment?.value)), lang)}`
                      : ` ${webFormatCurrency(Number(accountDetails?.lastPayment?.value), lang)}`
                    : `${webFormatCurrency(0, lang)}`,
              },
              {
                label: contentfulData?.lastPaymentReceivedOnTitle,
                tootiptext:
                  contentfulData?.lastPaymentReceivedOnInfoIconTooltipText,
                value:
                  accountDetails?.lastPaymentDate &&
                  moment(accountDetails?.lastPaymentDate).isValid()
                    ? moment(accountDetails?.lastPaymentDate).format(
                        'MMM D, YYYY'
                      )
                    : '--',
              },
            ].map(({ label, value, tootiptext }, index) => (
              <div
                key={label}
                className={`border-b border-gray-200 pb-[15px] sm:static relative`}
              >
                <CommanToolTip
                  label={label}
                  tooltipText={tootiptext}
                  isVisible={activeTooltip === index}
                  onToggle={() => handleTooltipToggle(index, 'card')}
                  fill={'#DA291C'}
                  index={index}
                  extraStyle={
                    (index == 3 || index == 4 || index == 5) && lang === 'fr'
                      ? 'w-[50%]'
                      : ''
                  }
                />
                <p
                  className={`font-bold sm:static absolute right-[10%] bottom-4  ${(index == 3 || index == 4 || index == 5) && lang === 'fr' && 'top-[9px]'}`}
                >
                  {value}
                </p>
              </div>
            ))}
          </div>
        </section>
      </div>

      {/* Alert Section */}
      <div
        className="bg-bg-body lg:p-5 sm:p-5 rounded-lg flex flex-col md:p-0"
        aria-labelledby="alert-section-heading"
      >
        <h3 id="alert-section-heading" className="sr-only">
          Alert Settings
        </h3>
        <div className="flex justify-between items-center">
          <div className="flex gap-2 w-full md:w-[75%]">
            <img
              src={assets?.account_alerts_icon}
              alt="account alert icon"
              height={'44'}
              className="hidden lg:block md:block w-[43px] md:w-[50px] lg:w-[43px]"
            />

            <div className="flex flex-col">
              <h3
                className="text-sm font-bold"
                aria-label={contentfulData?.manageAlertsTitle}
              >
                {contentfulData?.manageAlertsTitle}
              </h3>
              <div
                aria-label={renderDescriptionParagraphs(
                  contentfulData?.manageAlertsContent,
                  undefined,
                  true
                )}
              >
                {renderDescriptionParagraphs(
                  contentfulData?.manageAlertsContent,
                  'text-sm text-gray-500'
                )}
              </div>
            </div>
          </div>
          <button
            className={`${featureFlaggingData ? 'bg-primary-red text-white hover:bg-red-700' : 'text-gray-400 bg-gray-100 border-gray-300 border-2'} px-5 py-3 max-h-[48px] w-auto text-center hidden lg:block md:block`}
            aria-label={contentfulData?.manageAlertsCtaTitle}
            onClick={featureFlag}
          >
            <p className="text-sm font-[600]">
              {contentfulData?.manageAlertsCtaTitle}
            </p>
          </button>
          <span
            className="block lg:hidden md:hidden  ml-2 w-200px transition-opacity duration-2000 ease-in-out"
            onClick={featureFlag}
          >
            <RightArrow fill={featureFlaggingData ? '#DA291C' : '#ccc'} />
          </span>
        </div>
      </div>
    </div>
  );
};

export default AccountDetails;
